import React, { useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import paths from "@icarius-router/paths";
import CommonPage from "@icarius-common/commonPage";
import { getLocalizedString } from "@icarius-localization/strings";
import ProgressRenderer from "@icarius-table/renderersAndFilters/progressRenderer";
import ProgressFilter from "@icarius-table/renderersAndFilters/progressFilter";
import ProgressByRankingRendererDynamicColor from "@icarius-table/renderersAndFilters/progressByRankingRendererDynamicColor";
import FeedbackFormDialog from "@icarius-pages/feed/components/form/formDialog";
import { createFeedbackPublicationAction } from "@icarius-pages/feedback/actions";
import { MedalTable } from "@icarius-common/medalTable";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import usePerformanceInterviewAndFeedback from "./usePerformanceInterviewAndFeedback";
import InitialScreen from "./initialScreen";
import SummaryScreen from "./summaryScreen";
import FeedbackPublication from "./feedbackPublication";
import { getPerformanceInterviewAndFeedbackAction } from "../actions";

const PerformanceInterviewAndFeedback = () => {

  const {
    level,
    data,
    summaryData,
    isLoading,
    state,
    handlers,

    //feedback
    typeList,
    modeList,
    people,
    groups,
    tasks,
    goals,
    comments,
    isLoadingFeedback,

    isLoadingMedalTable,
  } = usePerformanceInterviewAndFeedback();

  const dispatch = useDispatch();
  const ownGridRef = useRef(); // tengo que usar una ref propia para el aggrid porque tengo 3 commonPage y al salir y volver se pierden las ref internas

  const goBackButton = () => (
    <CustomIconButton
      title={getLocalizedString("goBack")}
      onClick={handlers.handleUnselectProcess}
      type={"goBack"}
    />
  )

  const viewSummaryButton = () => (
    <CustomIconButton
      title={getLocalizedString("finalSummary")}
      onClick={() => handlers.handleOpenSummary(ownGridRef.current)}
      type={"assessment"}
    />
  )

  const downloadItem = () => (
    <CustomIconButton
      title={getLocalizedString("downloadFinalEvaluationSummary")}
      onClick={() => handlers.handleDownloadSummary(ownGridRef.current)}
      type={"download"}
    />
  )

  const feedbackButton = () => (
    <CustomIconButton
      title={getLocalizedString("giveFeedback")}
      onClick={() => handlers.handleOpenFeedbackForm(ownGridRef.current)}
      type={"feedback"}
    />
  )

  const medalTableItem = () => state.selectedProcess?.isPointsAssignationEnabled &&
    <CustomIconButton
      title={getLocalizedString("deliverPointsToRequestBenefits")}
      onClick={() => handlers.handleOpenMedalTable(ownGridRef.current)}
      type="medalTable"
    />

  const isLoadingEverything = isLoading || isLoadingFeedback.gridData || isLoadingFeedback.privacyData || isLoadingFeedback.feed || isLoadingMedalTable;

  const ownColumnDef = useMemo(() => {
    let columnDefinitions = getColumnDefByPage(paths.performanceInterviewAndFeedback);
    if (state.selectedProcess?.isPointsAssignationEnabled) return columnDefinitions;
    return columnDefinitions.filter((item) => !["pointsAssigned", "recognitionPlan"].includes(item.field))
  }, [state.selectedProcess])

  return (
    <>
      {
        !state.selectedProcess &&
        <InitialScreen
          isLoading={isLoadingEverything}
          data={data}
          handleSelectProcess={handlers.handleSelectProcess}
        />
      }
      {
        state.selectedEvaluation &&
        <SummaryScreen
          isLoading={isLoadingEverything}
          level={level}
          data={state.selectedEvaluation}
          summaryData={summaryData}
          process={state.selectedProcess}
          evaluatedName={state.selectedEvaluation?.evaluatedName}
          handleOpenViewFeedback={handlers.handleOpenViewFeedback}
          handleClose={handlers.handleCloseSummary}
        />
      }
      {
        state.selectedProcess && !state.selectedEvaluation &&
        <CommonPage
          isLoading={isLoadingEverything}
          title={`${getLocalizedString("performanceInterviewAndFeedback")}: ${state.selectedProcess.processName} - ${getLocalizedString("delegationDateFrom")} ${state.selectedProcess.fromDate} - ${getLocalizedString("delegationDateTo")} ${state.selectedProcess.endDate}`}
          gridTitle={`${getLocalizedString("performanceInterviewAndFeedback")} - ${state.selectedProcess.processName}`}
          ownColumnDef={ownColumnDef}
          rowData={state.selectedProcess?.data}
          menuItems={[goBackButton, viewSummaryButton, feedbackButton, medalTableItem, downloadItem]}
          ownGridRef={ownGridRef}
          hasExpand
          hasHelp
          frameworkComponents={{ ProgressByRankingRendererDynamicColor, ProgressRenderer, ProgressFilter }}
        >
        </CommonPage>
      }
      {
        state.feedbackFormData &&
        <FeedbackFormDialog
          open={Boolean(state.feedbackFormData)}
          preselectedData={{
            userCodes: [state.feedbackFormData?.evaluatedCode],
            type: 'ENT_DES',
            mode: 'P',
            reference: state.selectedProcess.processName,
            evaluationProcess: state.selectedProcess.process,
          }}
          data={null}
          goals={goals}
          modeList={modeList}
          typeList={typeList}
          groups={groups}
          tasks={tasks}
          people={people}
          statusToCreate={'E'}
          createPublicationAction={(dataToSend) => createFeedbackPublicationAction(dataToSend, true)}
          isLoading={isLoadingEverything}
          handleClose={handlers.handleCloseFeedbackForm}
          handleAfterCreateCallback={handlers.handleCreateFeedbackCallback}
          isFeedback
          isInterviewAndFeedback
          hideGoals
        />
      }
      {
        state.viewFeedbackIsOpen &&
        <FeedbackPublication
          isLoading={isLoadingFeedback}
          data={summaryData.feedback}
          people={people}
          groups={groups}
          tasks={tasks}
          comments={comments}
          handleClose={handlers.handleCloseViewFeedback}
        />
      }
      {
        Boolean(state.codeForMedalTable) &&
        <MedalTable
          userCode={state.codeForMedalTable}
          handleClose={handlers.handleCloseMedalTable}
          afterSubmitCallback={() => dispatch(getPerformanceInterviewAndFeedbackAction())}
          evaluationCode={state.selectedProcess.process}
          dialog
        />
      }
    </>
  )
}

export default PerformanceInterviewAndFeedback;